import { useRef, useState } from 'react';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseOutlined';
import UploadFileIcon from '@mui/icons-material/UploadFileOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import { CUSTOM_NAME_MAX, DOCUMENT_ACCEPT, documentFileProblem, MAX_DOCUMENT_MB } from '../utils/documentNames';

interface PendingFile {
  id: number;
  file: File;
  name: string;
  progress: number | null;
  done: boolean;
  error: string | null;
}

const nameFromFile = (file: File) =>
  file.name
    .replace(/\.[^.]+$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, CUSTOM_NAME_MAX);

const errorText = (err: unknown) =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Upload failed.') : 'Upload failed.';

/** "10. Other Documents" (Phase 103) — add one or more named documents. Each
 * chosen file becomes its own entry, named by the student (prefilled from
 * the file name). Files are checked for type and size first, then uploaded
 * one at a time with progress; a failure is shown on that file and leaves
 * the others alone. Shared by the Student Portal and the staff Final
 * Checklist. */
export const AddOtherDocumentsDialog = ({
  open,
  existingNames,
  onClose,
  upload,
  onUploaded,
}: {
  open: boolean;
  /** Names already in use (case-insensitive) — a new entry can't reuse one. */
  existingNames: string[];
  onClose: () => void;
  upload: (name: string, file: File, onProgress: (percent: number) => void) => Promise<unknown>;
  onUploaded: (names: string[]) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const nextId = useRef(1);
  const [files, setFiles] = useState<PendingFile[]>([]);
  const [problems, setProblems] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFiles([]);
    setProblems([]);
    setBusy(false);
  };
  const close = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const addFiles = (list: FileList | null) => {
    if (!list) return;
    const refused: string[] = [];
    const accepted: PendingFile[] = [];
    for (const file of Array.from(list)) {
      const problem = documentFileProblem(file);
      if (problem) refused.push(problem);
      else accepted.push({ id: nextId.current++, file, name: nameFromFile(file), progress: null, done: false, error: null });
    }
    setProblems(refused);
    setFiles((prev) => [...prev.filter((f) => !f.done), ...accepted]);
  };

  const update = (id: number, patch: Partial<PendingFile>) =>
    setFiles((prev) => prev.map((f) => (f.id === id ? { ...f, ...patch } : f)));

  const taken = new Set(existingNames.map((n) => n.toLowerCase()));
  const nameProblem = (f: PendingFile) => {
    const name = f.name.replace(/\s+/g, ' ').trim();
    if (!name) return 'Enter a name for this document.';
    if (name.length > CUSTOM_NAME_MAX) return `At most ${CUSTOM_NAME_MAX} characters.`;
    if (taken.has(name.toLowerCase())) return 'You already have a document with this name — use Replace on it instead.';
    const twins = files.filter((o) => !o.done && o.name.replace(/\s+/g, ' ').trim().toLowerCase() === name.toLowerCase());
    if (twins.length > 1) return 'Two files have this name — give each its own.';
    return null;
  };
  const pending = files.filter((f) => !f.done);
  const canSubmit = !busy && pending.length > 0 && pending.every((f) => !nameProblem(f));

  const submit = async () => {
    setBusy(true);
    const uploaded: string[] = [];
    for (const f of pending) {
      const name = f.name.replace(/\s+/g, ' ').trim();
      update(f.id, { progress: 0, error: null });
      try {
        await upload(name, f.file, (percent) => update(f.id, { progress: percent }));
        update(f.id, { progress: 100, done: true });
        uploaded.push(name);
      } catch (err) {
        update(f.id, { progress: null, error: errorText(err) });
      }
    }
    setBusy(false);
    if (uploaded.length > 0) onUploaded(uploaded);
    // Everything went up: close. Otherwise stay open on the failures.
    if (uploaded.length === pending.length) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={close} fullWidth maxWidth="sm" aria-labelledby="add-other-documents-title">
      <DialogTitle id="add-other-documents-title">Add other documents</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Upload any other document IMC has asked you for. Give each one a clear name — for example
            &ldquo;Bank statement&rdquo; or &ldquo;Sponsor letter&rdquo;. PDF, JPG, PNG, DOC or DOCX, up to{' '}
            {MAX_DOCUMENT_MB} MB each. Each document is reviewed by IMC after you upload it.
          </Typography>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept={DOCUMENT_ACCEPT}
            data-testid="other-documents-file"
            style={{ display: 'none' }}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = '';
            }}
          />
          <Box
            sx={{
              border: '2px dashed',
              borderColor: 'primary.light',
              borderRadius: 2,
              p: 2,
              textAlign: 'center',
              bgcolor: 'action.hover',
            }}
          >
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              disabled={busy}
              onClick={() => inputRef.current?.click()}
            >
              Choose files
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              You can choose several files at once.
            </Typography>
          </Box>
          {problems.map((p) => (
            <Alert key={p} severity="warning" onClose={() => setProblems((prev) => prev.filter((x) => x !== p))}>
              {p}
            </Alert>
          ))}
          {files.map((f) => {
            const problem = !f.done ? nameProblem(f) : null;
            return (
              <Box key={f.id} data-testid="other-document-pending" sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label="Document name"
                    size="small"
                    fullWidth
                    value={f.name}
                    disabled={busy || f.done}
                    error={!!problem}
                    helperText={problem ?? f.file.name}
                    inputProps={{ maxLength: CUSTOM_NAME_MAX + 1 }}
                    onChange={(e) => update(f.id, { name: e.target.value })}
                  />
                  {f.done ? (
                    <CheckCircleIcon color="success" sx={{ mt: 1 }} aria-label="Uploaded" />
                  ) : (
                    <IconButton
                      aria-label={`Remove ${f.file.name}`}
                      disabled={busy}
                      onClick={() => setFiles((prev) => prev.filter((o) => o.id !== f.id))}
                    >
                      <CloseIcon />
                    </IconButton>
                  )}
                </Stack>
                {f.progress !== null && !f.done && (
                  <LinearProgress variant="determinate" value={f.progress} sx={{ mt: 1, borderRadius: 1 }} />
                )}
                {f.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {f.error}
                  </Alert>
                )}
              </Box>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={!canSubmit}>
          {busy ? 'Uploading…' : pending.length > 1 ? `Upload ${pending.length} documents` : 'Upload'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
